/** Shared US-address type + form fields, used by first-run setup and My Account editing. */

export interface Address {
  /** Recipient name + phone — used for shipping (ship-to on the vendor PO). Optional for account addresses. */
  name?: string;
  phone?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export const emptyAddress = (): Address => ({
  name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
});

/**
 * Required fields present (line2 is optional). When `requireContact` is set (shipping addresses),
 * a recipient name + phone are also required so the vendor PO has someone to ship to.
 */
export function addressComplete(a: Address | undefined, requireContact = false): boolean {
  if (!(a && a.line1.trim() && a.city.trim() && a.state.trim() && a.postalCode.trim())) return false;
  if (requireContact && !(a.name?.trim() && a.phone?.trim())) return false;
  return true;
}

export const US_STATES: [string, string][] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
  ['PR', 'Puerto Rico'],
];

export function AddressFields({
  value,
  onChange,
  disabled,
  idPrefix,
  showContact,
  showErrors,
}: {
  value: Address;
  onChange: (a: Address) => void;
  disabled?: boolean;
  idPrefix: string;
  /** Show recipient name + phone (shipping addresses only). */
  showContact?: boolean;
  /** When set, required-missing fields render in red. */
  showErrors?: boolean;
}) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...value, [k]: e.target.value });
  // A required field is in error when showErrors is on and it's empty.
  const bad = (v: string | undefined) => Boolean(showErrors) && !(v ?? '').trim();
  const fieldCls = (v: string | undefined) => `field${bad(v) ? ' has-error' : ''}`;
  const ctlCls = (base: string, v: string | undefined) => `${base}${bad(v) ? ' invalid' : ''}`;
  return (
    <div className="address-grid" aria-disabled={disabled}>
      {showContact && (
        <>
          <label className={`${fieldCls(value.name)} address-full`}>
            <span className="field-label">Recipient name</span>
            <input className={ctlCls('input', value.name)} id={`${idPrefix}-name`} value={value.name ?? ''} onChange={set('name')}
              disabled={disabled} placeholder="Jane Smith" required autoComplete="name" aria-invalid={bad(value.name) || undefined} />
          </label>
          <label className={`${fieldCls(value.phone)} address-full`}>
            <span className="field-label">Phone</span>
            <input className={ctlCls('input', value.phone)} id={`${idPrefix}-phone`} value={value.phone ?? ''} onChange={set('phone')}
              disabled={disabled} placeholder="(555) 555-1234" required inputMode="tel" autoComplete="tel" aria-invalid={bad(value.phone) || undefined} />
          </label>
        </>
      )}
      <label className={`${fieldCls(value.line1)} address-full`}>
        <span className="field-label">Street address</span>
        <input className={ctlCls('input', value.line1)} id={`${idPrefix}-line1`} value={value.line1} onChange={set('line1')}
          disabled={disabled} placeholder="123 Main St" required autoComplete="address-line1" aria-invalid={bad(value.line1) || undefined} />
      </label>
      <label className="field address-full">
        <span className="field-label">Apt / Suite <span className="opt">(optional)</span></span>
        <input className="input" id={`${idPrefix}-line2`} value={value.line2 ?? ''} onChange={set('line2')}
          disabled={disabled} placeholder="Suite 200" autoComplete="address-line2" />
      </label>
      <label className={fieldCls(value.city)}>
        <span className="field-label">City</span>
        <input className={ctlCls('input', value.city)} id={`${idPrefix}-city`} value={value.city} onChange={set('city')}
          disabled={disabled} required autoComplete="address-level2" aria-invalid={bad(value.city) || undefined} />
      </label>
      <label className={fieldCls(value.state)}>
        <span className="field-label">State</span>
        <select className={ctlCls('select', value.state)} id={`${idPrefix}-state`} value={value.state} onChange={set('state')}
          disabled={disabled} required autoComplete="address-level1" aria-invalid={bad(value.state) || undefined}>
          <option value="" disabled>Select…</option>
          {US_STATES.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}
        </select>
      </label>
      <label className={fieldCls(value.postalCode)}>
        <span className="field-label">ZIP code</span>
        <input className={ctlCls('input', value.postalCode)} id={`${idPrefix}-zip`} value={value.postalCode} onChange={set('postalCode')}
          disabled={disabled} required inputMode="numeric" autoComplete="postal-code" placeholder="12345" aria-invalid={bad(value.postalCode) || undefined} />
      </label>
    </div>
  );
}
